import React, { useEffect, useRef, useState } from 'react';

import { useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

import PinPage from '../../components/PinPage/PinPage';

import { getReferralLink, pinAttemptCounter, validatePin } from '../../services/authApi/authApi';
import { forgotPinPasswordLinkApi } from '../../services/authApi/forgotPinApi';
import { addUserData } from '../../store/reducer/accountSlice';
import { toastNotify } from '../../utils/toastNotify';

import './Login.scss';

const EnterPin = () => {
  const [loading, setLoading] = useState(false);
  const [forgotPinLoading, setForgotPinLoading] = useState(false);
  const [pin, setPin] = useState('');
  const [attemptCount, setAttemptCount] = useState(0);
  const [countDown, setCountDown] = useState(0);
  const [seconds, setSeconds] = useState(300);
  const [disabledCheckbox, setDisabledCheckbox] = useState(false);
  let navigate = useNavigate();
  const search = useLocation().search;
  const token = new URLSearchParams(search).get('token');
  const dispatch = useDispatch();
  const shouldLog = useRef(true);
  useEffect(() => {
    if (token) {
      localStorage.setItem('access_token_temp', token);
      window.history.replaceState(null, window.location.pathname, window.location.pathname);
    } else if (!token && !localStorage.getItem('access_token_temp')) {
      navigate('/login', { replace: true });
    }
  }, []);

  const getAttemptCount = async (token) => {
    const pinCounter = await pinAttemptCounter(token);
    setAttemptCount(pinCounter.data.loginRetryAttempt);
    if (pinCounter.data.loginRetryAttempt === 3) {
      timerCounter();
    } else if (pinCounter.data.loginRetryAttempt === 4) {
      fiveMinTimeCounter();
    } else if (pinCounter.data.loginRetryAttempt === 5) {
      navigate('/login');
    }
  };

  useEffect(() => {
    if (shouldLog.current) {
      shouldLog.current = false;
      if (token) {
        localStorage.setItem('access_token_temp', token);
        window.history.replaceState(null, window.location.pathname, window.location.pathname);
      } else {
        getAttemptCount(localStorage.getItem('access_token_temp'));
      }
    }
  }, []);

  const timerCounter = (timerValue = 30) => {
    let timeleft = timerValue;
    setDisabledCheckbox(true);
    const downloadTimer = setInterval(() => {
      if (timeleft <= 0) {
        clearInterval(downloadTimer);
        setCountDown(0);
        setDisabledCheckbox(false);
      } else {
        if (timeleft < 10) {
          timeleft = 0 + '' + timeleft;
        }
        setCountDown(timeleft);
      }
      timeleft -= 1;
    }, 1000);
  };

  const fiveMinTimeCounter = () => {
    const intervalId = setInterval(() => {
      setDisabledCheckbox(true);
      setSeconds((prevSeconds) => {
        if (prevSeconds === 0) {
          setDisabledCheckbox(false);
          clearInterval(intervalId); // Stop the timer when it reaches zero
          // You can add additional logic here when the countdown reaches zero
        }
        return prevSeconds - 1;
      });
    }, 1000);
  };

  const fetchReferralLink = async (token) => {
    await getReferralLink(token);
  };

  const handleEnterPin = async (pin) => {
    setPin(pin);
    if (pin.length === 4) {
      let formData = {
        pin: pin,
        pinVerificationType: 'login',
      };
      try {
        const userData = await validatePin(formData);
        if (!userData.isError) {
          // validate-pin's response already carries the full KYC/AML/onboarding fields, same as the profile API,
          // so the profile can be considered loaded immediately without waiting on the separate profile fetch.
          // counter is dispatched in the same action as userData so isOnboardingComplete never sees a stale counter.
          dispatch(addUserData({ userData: userData?.data?.user, isPinEntered: true, isProfileLoaded: true, counter: userData?.data?.counter }));
          localStorage.setItem('access_token', userData?.data?.accessToken);
          await fetchReferralLink(userData?.data?.accessToken);
          const array = new Uint8Array(16);
          window.crypto.getRandomValues(array); 
          const randomValue = Array.from(array, (byte) => byte.toString(16).padStart(2, '0')).join('');
          if(process.env.REACT_APP_WEBFLOW_LOGIN=== 'production'){
            document.cookie = `user_login=${randomValue}; expires=Fri, 31 Dec 9999 23:59:59 GMT; path=/; domain=.realbricks.com`;
          }
          localStorage.setItem('restrictPage', false);
          localStorage.removeItem('access_token_temp');
          localStorage.removeItem('tabClosingTime');
          const redirectPath = localStorage.getItem('redirectIntendedPage');
          if (redirectPath && redirectPath !== '/' && redirectPath !== '/login') {
            navigate(redirectPath);
            // Clear the saved path after redirect
            localStorage.removeItem('redirectIntendedPage');
          } else if (localStorage.getItem('prev_pathname')) {
            navigate(localStorage.getItem('prev_pathname'));
            localStorage.removeItem('prev_pathname');
          } else {
            navigate('/');
          }
          localStorage.removeItem('counter');
        } else {
          setPin('');
          toastNotify('error', userData?.message);
          setAttemptCount(userData?.data?.loginRetryAttempt);
        }
      } catch (error) {
        setPin('');
        const { response } = error;
        setAttemptCount(response?.data?.data?.loginRetryAttempt);
        if (response?.data?.data?.loginRetryAttempt === 3) {
          timerCounter();
        } else if (response?.data?.data?.loginRetryAttempt === 4) {
          fiveMinTimeCounter();
        } else if (response?.data?.data?.loginRetryAttempt === 5) {
          localStorage.clear();
          localStorage.removeItem('redirectIntendedPage');
          navigate('/login');
        }
        toastNotify('error', response?.data?.message);
      } finally {
        setLoading(false);
      }
    }
  };

  const sendVerificationLink = async () => {
    let formData = {
      email: localStorage.getItem('email'),
      credentialType: 'pinAndPassword',
    };
    setForgotPinLoading(true);
    try {
      const userData = await forgotPinPasswordLinkApi(formData);
      if (!userData.isError) {
        navigate('/forgot-pin/request-reset');
        localStorage.removeItem('counter');
      } else {
        toastNotify('error', userData?.message);
      }
    } catch (error) {
      const { response } = error;
      toastNotify('error', response?.data?.message);
    } finally {
      setForgotPinLoading(false);
    }
  };

  return (
    <div className='auth auth--details auth--no-info-mobile login'>
      <PinPage
        pin={pin}
        authInfoTitle={'Welcome back'}
        authInfoDescription={'Enter your credentials to access your Realbricks account.'}
        authFormTitle={'Enter your 4-digit PIN'}
        setPin={(value) => handleEnterPin(value)}
        loading={loading}
        linkLoading={forgotPinLoading}
        linkName={'I forgot my PIN'}
        linkReDirection={Number(localStorage.getItem('counter')) >= 5 && 'forgot-pin'}
        onLinkClick={() => Number(localStorage.getItem('counter')) < 5 && sendVerificationLink()}
        attemptCount={attemptCount}
        countDown={countDown}
        seconds={seconds}
        disabledCheckbox={disabledCheckbox}
      />
    </div>
  );
};

export default EnterPin;
